import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function DeclareResultsModal({ match, session, onClose, onSuccess }) {
  const [registrations, setRegistrations] = useState([]);
  const [winners, setWinners] = useState([]);
  const [finishPoints, setFinishPoints] = useState({}); // New state for finish points by rank
  const [prizePool, setPrizePool] = useState({
    1: 50000, // 500 INR in paise
    2: 30000,
    3: 20000,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRegistrations();
  }, [match.id]);

  const fetchRegistrations = async () => {
    try {
      const token = session?.access_token || localStorage.getItem('admin_token');

      const response = await fetch(`${API_BASE}/admin/matches/${match.id}/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const addWinner = () => {
    setWinners([...winners, { rank: winners.length + 1, user_id: '', team_id: '' }]);
  };

  const removeWinner = (index) => {
    setWinners(winners.filter((_, i) => i !== index));
  };

  const updateWinner = (index, field, value) => {
    const newWinners = [...winners];
    newWinners[index][field] = value;

    // Clear the other field
    if (field === 'user_id') {
      newWinners[index].team_id = '';
    } else if (field === 'team_id') {
      newWinners[index].user_id = '';
    }

    setWinners(newWinners);
  };

  const updatePrize = (rank, amount) => {
    setPrizePool({
      ...prizePool,
      [rank]: parseInt(amount) || 0
    });
  };

  const updateFinishPoints = (rank, points) => {
    setFinishPoints({
      ...finishPoints,
      [rank]: parseInt(points) || 0
    });
  };

  const handleDeclareResults = async () => {
    // Validation
    if (winners.length === 0) {
      toast.error('Please add at least one winner');
      return;
    }

    for (const winner of winners) {
      if (!winner.user_id && !winner.team_id) {
        toast.error(`Please select a winner for Rank ${winner.rank}`);
        return;
      }
    }

    setLoading(true);
    try {
      // Build prize breakdown (only for ranks with winners)
      const prizeBreakdown = {};
      winners.forEach(winner => {
        if (prizePool[winner.rank]) {
          prizeBreakdown[winner.rank] = prizePool[winner.rank];
        }
      });

      // Build winners array with details for display
      const winnersData = winners.map(winner => {
        const prizeAmount = parseInt(prizePool[winner.rank] || 0);
        const points = parseInt(finishPoints[winner.rank] || 0);
        let winnerName = 'Unknown';

        // Find registration to get name
        const regId = winner.user_id || winner.team_id;
        const reg = registrations.find(r => r.user_id === regId || r.team_id === regId);

        if (reg) {
          winnerName = reg.bgm_username || reg.team_name || 'Player';
        }

        return {
          rank: winner.rank,
          ...(winner.user_id ? { user_id: winner.user_id } : { team_id: winner.team_id }),
          winner_name: winnerName,
          prize_amount: prizeAmount,
          finish_points: points
        };
      });

      const token = session?.access_token || localStorage.getItem('admin_token');

      const response = await fetch(`${API_BASE}/admin/matches/${match.id}/declare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          winners: winnersData,
          prize_breakdown: prizeBreakdown
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to declare results');
      }

      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to declare results');
    } finally {
      setLoading(false);
    }
  };

  const getTotalPrizePool = () => {
    return Object.values(prizePool).reduce((sum, prize) => sum + (parseInt(prize) || 0), 0);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}
      >
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Declare Results</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '5px' }}>
              {match.title}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div>
          {/* Prize Pool Summary */}
          <div style={{
            padding: '20px',
            background: 'var(--accent-bg)',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>
              💰 Total Prize Pool
            </h4>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              ₹{(getTotalPrizePool() / 100).toFixed(2)}
            </div>
          </div>

          {/* Winners Section */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h4 style={{ color: 'var(--primary-color)' }}>🏆 Winners</h4>
              <button className="btn btn-primary" onClick={addWinner} style={{ padding: '6px 12px', fontSize: '0.9rem' }}>
                ➕ Add Winner
              </button>
            </div>

            {winners.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '30px',
                background: 'var(--accent-bg)',
                borderRadius: '8px',
                color: 'var(--text-secondary)'
              }}>
                No winners added yet. Click "Add Winner" to start.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {winners.map((winner, index) => (
                  <div key={index} style={{
                    padding: '15px',
                    background: 'var(--accent-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '15px'
                    }}>
                      <h5 style={{ fontSize: '1.1rem' }}>
                        Rank #{winner.rank}
                      </h5>
                      <button
                        onClick={() => removeWinner(index)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--danger-color)',
                          cursor: 'pointer',
                          fontSize: '1.2rem'
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    <div className="form-group" style={{ marginBottom: '15px' }}>
                      <label className="form-label">Select Winner</label>
                      <select
                        className="form-input"
                        value={winner.user_id || winner.team_id || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const selectedReg = registrations.find(r =>
                            r.user_id === value || r.team_id === value
                          );
                          if (selectedReg) {
                            if (selectedReg.team_id) {
                              updateWinner(index, 'team_id', selectedReg.team_id);
                            } else {
                              updateWinner(index, 'user_id', selectedReg.user_id);
                            }
                          }
                        }}
                      >
                        <option value="">-- Select Winner --</option>
                        {registrations.filter(reg => {
                          const regValue = reg.user_id || reg.team_id;
                          // Check if this user/team is selected in ANY OTHER winner entry
                          const isSelectedElsewhere = winners.some((w, wIndex) =>
                            wIndex !== index && (w.user_id === regValue || w.team_id === regValue)
                          );
                          return !isSelectedElsewhere;
                        }).map((reg) => (
                          <option key={reg.id} value={reg.user_id || reg.team_id}>
                            [Slot #{reg.assigned_slot || '?'}] - {reg.bgm_username || reg.user_id.slice(0, 8)}
                            {reg.team_members && reg.team_members.length > 0
                              ? ` (Team: ${reg.team_name ? reg.team_name + ' - ' : ''}${reg.team_members.join(', ')})`
                              : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Prize Amount (₹)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={(prizePool[winner.rank] || 0) / 100}
                        onChange={(e) => updatePrize(winner.rank, parseFloat(e.target.value) * 100)}
                        min="0"
                        step="0.01"
                      />
                      <p style={{
                        fontSize: '0.85rem',
                        color: 'var(--text-secondary)',
                        marginTop: '5px'
                      }}>
                        Amount in paise: {prizePool[winner.rank] || 0}
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Finish Points (Kills)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={finishPoints[winner.rank] || 0}
                        onChange={(e) => updateFinishPoints(winner.rank, e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              className="btn btn-outline"
              onClick={onClose}
              style={{ flex: 1 }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn btn-success"
              onClick={handleDeclareResults}
              style={{ flex: 1 }}
              disabled={loading || winners.length === 0}
            >
              {loading ? 'Declaring...' : '🏆 Declare Results'}
            </button>
          </div>

          <p style={{
            textAlign: 'center',
            marginTop: '15px',
            fontSize: '0.85rem',
            color: 'var(--text-secondary)'
          }}>
            ⚠️ Results cannot be modified once declared
          </p>
        </div>
      </div>
    </div>
  );
}

export default DeclareResultsModal;
